import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Button,
  Skeleton,
  Alert,
  Divider,
  Stack,
  Avatar
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon,
  Language as WebsiteIcon
} from '@mui/icons-material';
import { businessProfileApi } from '../../services/businessProfileApi';

const BusinessProfileSelector = ({ 
  onProfileSelect, 
  selectedProfileId = null,
  websiteUrl = '',
  disabled = false,
  onProfilesLoaded = () => {} // Callback when profiles are loaded
}) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedValue, setSelectedValue] = useState(selectedProfileId ? `existing-${selectedProfileId}` : 'create-new');

  useEffect(() => {
    fetchBusinessProfiles();
  }, []);

  useEffect(() => {
    if (selectedProfileId) {
      setSelectedValue(`existing-${selectedProfileId}`);
    }
  }, [selectedProfileId]);

  const fetchBusinessProfiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await businessProfileApi.getProfilesForSelection();
      
      if (response.success) {
        const profilesData = response.data.profiles || [];
        setProfiles(profilesData);
        
        // Notify parent component about loaded profiles
        onProfilesLoaded(profilesData);
      } else {
        throw new Error('Failed to fetch business profiles');
      }
    } catch (err) {
      console.error('Failed to fetch business profiles:', err);
      setError('Failed to load business profiles');
      
      // Notify parent that no profiles were loaded (empty array)
      onProfilesLoaded([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);

    if (value === 'create-new') {
      onProfileSelect(null); // No profile selected, will create new
    } else if (value.startsWith('existing-')) {
      const profileId = parseInt(value.replace('existing-', ''));
      const selectedProfile = profiles.find(p => p.id === profileId);
      onProfileSelect(selectedProfile);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Choose Business Profile
        </Typography>
        <Stack spacing={2}>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={120} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Choose Business Profile
        </Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchBusinessProfiles} variant="outlined">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choose Business Profile
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select an existing business profile or create a new one from your website
      </Typography>

      <RadioGroup
        value={selectedValue}
        onChange={handleSelectionChange}
        disabled={disabled}
      >
        {/* Create New Option */}
        <Card 
          variant="outlined" 
          sx={{ 
            mb: 2,
            backgroundColor: selectedValue === 'create-new' ? 'action.selected' : 'background.paper',
            borderColor: selectedValue === 'create-new' ? 'primary.main' : 'divider',
            borderWidth: selectedValue === 'create-new' ? 2 : 1
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <FormControlLabel
              value="create-new"
              control={<Radio />}
              label={
                <Box sx={{ ml: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.main' }}>
                      <AddIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Create New Business Profile
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Analyze {websiteUrl || 'your website'} to create a new profile
                      </Typography>
                    </Box>
                  </Box>
                  {websiteUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 5 }}>
                      <WebsiteIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {websiteUrl}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
              sx={{ width: '100%', margin: 0 }}
            />
          </CardContent>
        </Card>

        {/* Existing Profiles */}
        {profiles.length > 0 && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                OR SELECT EXISTING PROFILE
              </Typography>
            </Divider>

            {profiles.map((profile) => {
              const isSelected = selectedValue === `existing-${profile.id}`;
              
              return (
                <Card
                  key={profile.id}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderWidth: isSelected ? 2 : 1
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <FormControlLabel
                      value={`existing-${profile.id}`}
                      control={<Radio />}
                      label={
                        <Box sx={{ ml: 1, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                              <Avatar 
                                sx={{ 
                                  width: 40, 
                                  height: 40, 
                                  mr: 2, 
                                  bgcolor: 'secondary.main',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {getInitials(profile.name)}
                              </Avatar>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {profile.name}
                                  </Typography>
                                  {profile.is_primary && (
                                    <Chip
                                      icon={<StarIcon />}
                                      label="Primary"
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ ml: 1, height: 20 }}
                                    />
                                  )}
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {profile.description}
                                </Typography>

                                {/* Profile Stats */}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                  {profile.industry && (
                                    <Chip
                                      label={profile.industry}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                  {profile.has_brand_assets && (
                                    <Chip
                                      icon={<CheckIcon />}
                                      label="Brand Assets"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                  {profile.has_complete_profile && (
                                    <Chip
                                      icon={<CheckIcon />}
                                      label="Complete"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WebsiteIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {profile.url}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    Updated {formatDate(profile.updated_at)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      }
                      sx={{ width: '100%', margin: 0 }}
                    />
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}
      </RadioGroup>

      {profiles.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          You don't have any existing business profiles. A new one will be created from your website.
        </Alert>
      )}
    </Box>
  );
};

export default BusinessProfileSelector; 